import React, { useState, useMemo, useCallback } from "react";
import { Lesson } from "../../types/education";
import { CurriculumService } from "../../services/curriculumService";
import { SearchFilter } from "../../types/curriculum";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Search,
  Filter,
  Clock,
  BookOpen,
  Star,
  CheckCircle,
  Lock,
  Target,
} from "lucide-react";

interface CourseCatalogProps {
  curriculumService: CurriculumService;
  lessons: Lesson[];
  completedLessons: string[];
  bookmarkedLessons: string[];
  onLessonSelect: (lessonId: string) => void;
  onBookmarkToggle: (lessonId: string) => void;
}

export const CourseCatalog: React.FC<CourseCatalogProps> = ({
  curriculumService,
  lessons,
  completedLessons,
  bookmarkedLessons,
  onLessonSelect,
  onBookmarkToggle,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<SearchFilter>({});
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<"catalog" | "recommendations">(
    "catalog",
  );

  // Get all available topics and learning paths
  const allTopics = useMemo(
    () => curriculumService.getAllTopics(),
    [curriculumService],
  );

  // Get unique topic names and tags for filters
  const topicOptions = useMemo(() => {
    const rootTopics = allTopics.filter((topic) => topic.level === 0);
    return rootTopics.map((topic) => ({
      id: topic.id,
      name: topic.name,
      count: allTopics.filter((t) => t.parentId === topic.id).length,
    }));
  }, [allTopics]);

  const tagOptions = useMemo(() => {
    const allTags = allTopics.flatMap((topic) => topic.tags);
    return [...new Set(allTags)].sort();
  }, [allTopics]);

  // Search and filter lessons
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() && Object.keys(filters).length === 0) {
      return lessons.map((lesson) => ({
        content: {
          id: lesson.id,
          type: "lesson" as const,
          title: lesson.title,
          description: lesson.description,
          topics: curriculumService
            .getTopicsForLesson(lesson.id)
            .map((t) => t.id),
          difficulty: lesson.difficulty,
          estimatedDuration: parseInt(lesson.duration) || 0,
          tags: curriculumService
            .getTopicsForLesson(lesson.id)
            .flatMap((t) => t.tags),
          keywords: [lesson.category],
          lastUpdated: new Date().toISOString(),
          version: "1.0.0",
          searchableText: `${lesson.title} ${lesson.description}`.toLowerCase(),
        },
        relevanceScore: 1,
        matchHighlights: [],
      }));
    }

    return curriculumService.searchContent(searchQuery, filters);
  }, [searchQuery, filters, lessons, curriculumService]);

  // Get personalized recommendations
  const recommendedLessons = useMemo(() => {
    const completedTopics = completedLessons.flatMap((lessonId) =>
      curriculumService.getTopicsForLesson(lessonId).map((t) => t.id),
    );
    const recommendedTopics = curriculumService.getRecommendedNextTopics(
      completedTopics,
      {},
    );

    const seen = new Set<string>();
    return recommendedTopics
      .slice(0, 6)
      .flatMap((topic) => curriculumService.getLessonsForTopic(topic.id))
      .filter((lesson) => {
        if (seen.has(lesson.id)) return false;
        seen.add(lesson.id);
        return true;
      })
      .slice(0, 8);
  }, [completedLessons, curriculumService]);

  // Filter handlers
  const handleFilterChange = useCallback(
    (key: keyof SearchFilter, value: unknown) => {
      setFilters((prev: SearchFilter) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchQuery("");
  }, []);

  const toggleArrayFilter = useCallback(
    (key: keyof SearchFilter, value: string) => {
      setFilters((prev: SearchFilter) => {
        const currentArray = (prev[key] as string[]) || [];
        const newArray = currentArray.includes(value)
          ? currentArray.filter((item) => item !== value)
          : [...currentArray, value];
        return { ...prev, [key]: newArray };
      });
    },
    [],
  );

  // Get lesson status
  const getLessonStatus = useCallback(
    (lessonId: string) => {
      const isCompleted = completedLessons.includes(lessonId);
      const isBookmarked = bookmarkedLessons.includes(lessonId);
      const lessonTopics = curriculumService.getTopicsForLesson(lessonId);
      const isLocked = lessonTopics.some(
        (topic) =>
          !curriculumService.canAccessTopic(
            topic.id,
            completedLessons.flatMap((id) =>
              curriculumService.getTopicsForLesson(id).map((t) => t.id),
            ),
          ),
      );

      return { isCompleted, isBookmarked, isLocked };
    },
    [completedLessons, bookmarkedLessons, curriculumService],
  );

  // Render lesson card
  const renderLessonCard = useCallback(
    (lesson: Lesson, relevanceScore?: number, keyPrefix?: string) => {
      const { isCompleted, isBookmarked, isLocked } = getLessonStatus(
        lesson.id,
      );
      const lessonTopics = curriculumService.getTopicsForLesson(lesson.id);

      return (
        <Card
          key={keyPrefix || `catalog-${lesson.id}`}
          className={`cursor-pointer transition-all hover:shadow-md ${isLocked ? "opacity-75" : ""}`}
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-lg flex items-center gap-2">
                  {lesson.title}
                  {isCompleted && (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                  {isLocked && <Lock className="h-4 w-4 text-gray-400" />}
                  {relevanceScore && relevanceScore > 5 && (
                    <Star className="h-4 w-4 text-yellow-500" />
                  )}
                </CardTitle>
                <CardDescription className="mt-1">
                  {lesson.description}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onBookmarkToggle?.(lesson.id);
                }}
                className="ml-2"
                title={isBookmarked ? "Remove bookmark" : "Add bookmark"}
                aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
              >
                <Star
                  className={`h-4 w-4 ${isBookmarked ? "fill-yellow-400 text-yellow-400" : "text-gray-400"}`}
                />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Metadata */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {lesson.duration}
                </div>
                <div className="flex items-center gap-1">
                  <BookOpen className="h-3 w-3" />
                  {lesson.difficulty}
                </div>
                <Badge
                  variant={isCompleted ? "secondary" : "outline"}
                  className="text-xs"
                >
                  {isCompleted
                    ? "Completed"
                    : isLocked
                      ? "Locked"
                      : "Available"}
                </Badge>
              </div>

              {/* Topics */}
              <div className="flex flex-wrap gap-1">
                {lessonTopics.slice(0, 3).map((topic) => (
                  <Badge key={topic.id} variant="outline" className="text-xs">
                    {topic.name}
                  </Badge>
                ))}
                {lessonTopics.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{lessonTopics.length - 3} more
                  </Badge>
                )}
              </div>

              {/* Action button */}
              <Button
                className="w-full"
                disabled={isLocked}
                onClick={() => onLessonSelect?.(lesson.id)}
              >
                {isCompleted
                  ? "Review"
                  : isLocked
                    ? "Complete Prerequisites"
                    : "Start Lesson"}
              </Button>
              {isLocked && lesson.prerequisites && lesson.prerequisites.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Requires: {lesson.prerequisites.map((prereqId) => {
                    const prereqLesson = lessons.find((l) => l.id === prereqId);
                    return prereqLesson?.title || prereqId;
                  }).join(", ")}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      );
    },
    [getLessonStatus, curriculumService, onBookmarkToggle, onLessonSelect, lessons],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Course Catalog</h2>
          <p className="text-muted-foreground">
            Explore our comprehensive color theory curriculum
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === "catalog" ? "default" : "outline"}
            onClick={() => setViewMode("catalog")}
          >
            <BookOpen className="h-4 w-4 mr-2" />
            All Courses
          </Button>
          <Button
            variant={viewMode === "recommendations" ? "default" : "outline"}
            onClick={() => setViewMode("recommendations")}
          >
            <Target className="h-4 w-4 mr-2" />
            Recommended
          </Button>
        </div>
      </div>

      {viewMode === "catalog" ? (
        <>
          {/* Search and Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search & Filter Courses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search courses, topics, or keywords..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchQuery(e.target.value)
                  }
                  className="pl-10"
                />
              </div>

              {/* Filter Toggle */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  {Object.keys(filters).length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {Object.keys(filters).length}
                    </Badge>
                  )}
                </Button>
                {(Object.keys(filters).length > 0 || searchQuery) && (
                  <Button variant="outline" onClick={clearFilters}>
                    Clear All
                  </Button>
                )}
              </div>

              {/* Filter Options */}
              {showFilters && (
                <div className="space-y-4 pt-4 border-t">
                  {/* Difficulty Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Difficulty
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {["beginner", "intermediate", "advanced"].map(
                        (difficulty) => (
                          <Button
                            key={difficulty}
                            variant={
                              filters.difficulty?.includes(difficulty as "beginner" | "intermediate" | "advanced")
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() =>
                              toggleArrayFilter("difficulty", difficulty)
                            }
                          >
                            {difficulty}
                          </Button>
                        ),
                      )}
                    </div>
                  </div>

                  {/* Topic Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Topics
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {topicOptions.map((topic) => (
                        <Button
                          key={topic.id}
                          variant={
                            filters.topics?.includes(topic.id)
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => toggleArrayFilter("topics", topic.id)}
                        >
                          {topic.name} ({topic.count})
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Tags Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {tagOptions.map((tag) => (
                        <Button
                          key={tag}
                          variant={
                            filters.tags?.includes(tag) ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => toggleArrayFilter("tags", tag)}
                        >
                          {tag}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Duration Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Duration (minutes)
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <label className="text-sm">Min:</label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={filters.duration?.min || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleFilterChange("duration", {
                              ...filters.duration,
                              min: e.target.value
                                ? parseInt(e.target.value)
                                : undefined,
                            })
                          }
                          className="w-20"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm">Max:</label>
                        <Input
                          type="number"
                          placeholder="120"
                          value={filters.duration?.max || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleFilterChange("duration", {
                              ...filters.duration,
                              max: e.target.value
                                ? parseInt(e.target.value)
                                : undefined,
                            })
                          }
                          className="w-20"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {searchResults.length}{" "}
                {searchResults.length === 1 ? "Course" : "Courses"} Found
              </h3>
              {searchQuery && (
                <p className="text-sm text-muted-foreground">
                  Searching for "{searchQuery}"
                </p>
              )}
            </div>

            {searchResults.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {searchResults.map((result) => {
                  const lesson = lessons.find(
                    (l) => l.id === result.content.id,
                  );
                  return lesson
                    ? renderLessonCard(lesson, result.relevanceScore)
                    : null;
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium">No courses found</h3>
                    <p className="text-muted-foreground">
                      Try adjusting your search terms or filters
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      ) : (
        /* Recommendations View */
        <div>
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Recommended for You</h3>
            <p className="text-muted-foreground">
              Based on your progress and learning goals
            </p>
          </div>

          {recommendedLessons.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {recommendedLessons.map((lesson) =>
                renderLessonCard(lesson, undefined, `recommended-${lesson.id}`),
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">
                    No recommendations yet
                  </h3>
                  <p className="text-muted-foreground">
                    Start completing lessons to get personalized recommendations
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
